import type {
  ActionContext,
  IInteractivityTracker,
  InteractivityTrackerStatus,
  nullable,
} from "@1js/office-online-interactivity-interop";

/**
 * Keep single instance tracker global
 */
declare global {
  // eslint-disable-next-line  no-var
  var __InteractivityTracker__: IInteractivityTracker;
}

/**
 * Track interactivity performance of user action input event.
 * Notice: calls to trackEventResponseTime should happen only after InteractivityTracker it has been initialized by the app.
 *
 * Example usage:
 * ```
 * import { trackEventResponseTime } from '@1js/office-online-interactivity/lib/interactivityTrackerExt';
 *
 * document.getElementById('menu1Element').addEventListener('click', e => {
 *    trackEventResponseTime(e, 'menu1_open');
 * }
 *
 * if async UI is passed, it's assumed that an element timing event will be fired when the async UI is ready.
 * For more info on async UI tracking see README.md
 * ```
 *
 * @param event The DOM event passed to event handler
 * @param userAction An identifying name of the user action
 * @param context Array of action context information
 * @param asyncUiNames Array of async UI names
 * @return userActionKey. This key can be used to add meta data to userAction via addContextToAction
 */
export function trackEventResponseTime(
  event: Event,
  userAction: string,
  context: ActionContext[] | undefined = undefined,
  asyncUiNames: string[] | undefined = undefined
): string | undefined {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    return undefined;
  }
  return trackerInstance.trackInteraction(
    event,
    userAction,
    context,
    asyncUiNames
  )!;
}

/**
 * Adds contexts for userAction which is being tracked using @param userActionKey
 *
 * @param userActionKey - this key can be obtained when the user action track is initiated using {trackEventResponseTime}
 * @param itemKey - name of the data being recorded.
 * @param value - value of the recorded data
 */
export function addContextToAction(
  userActionKey: string,
  itemKey: string,
  value: string | nullable
) {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (trackerInstance) {
    trackerInstance.addContext(userActionKey, itemKey, value);
  }
}

/**
 * Reports the trackedUserActions report map for the user actions and their total and slow counts
 *
 * This is being invoked for logging the interactivity metrics for the PAS tests
 * @returns the trackedUserActionsReport if it exists, "undefined" otherwise
 */
export function trackedUserActionsReport() {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (trackerInstance) {
    return trackerInstance.trackedUserActionsReport();
  }
  return undefined;
}

/**
 * Reports the current app state
 *
 * This is being invoked for logging the interactivity metrics for the PAS tests
 * @returns the recentAppState if set, "undefined" otherwise
 */
export function recentAppState() {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (trackerInstance) {
    return trackerInstance.recentAppState();
  }
  return undefined;
}

/**
 * Gets Event object and checks if its supported by the interactivity tracker.
 */
export function isEventSupported(rawEvent: Event): boolean {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    return false;
  }
  return trackerInstance.isEventSupportedForTracking(rawEvent);
}

/**
 * Start tracking animation with animationName
 * If rawEvent is undefined, the current time will be used
 *
 * @param rawEvent event that started the animation
 * @param animationName animation name
 * @returns true if tracking started, false otherwise
 */
export function startTrackAnimation(
  rawEvent: Event | nullable,
  animationName: string
): boolean {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    return false;
  }
  return trackerInstance.startTrackAnimation(rawEvent, animationName);
}

/**
 * Stop tracking animation with animationName
 *
 * @param animationName animation name
 */
export function stopTrackAnimation(
  animationName: string,
  abort: boolean = false
): void {
  // lookup the single global instance of event tracker
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    return;
  }
  trackerInstance.stopTrackAnimation(animationName, abort);
}

/**
 * @param asyncUiName UI name to check if it's allowed to be tracked
 * @returns boolean
 */
export function isAsyncUiNameAllowed(asyncUiName: string): boolean {
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    // When there is no tracker instance, we should not allow any async UI name
    return false;
  }
  return trackerInstance.isAsyncUiNameAllowed(asyncUiName);
}

/**
 * @returns InteractivityTrackerStatus
 */
export function getInteractivityTrackerStatus():
  | InteractivityTrackerStatus
  | undefined {
  const trackerInstance: IInteractivityTracker =
    window.__InteractivityTracker__;
  if (!trackerInstance) {
    return undefined;
  }
  return trackerInstance.getStatus();
}
